import { JSX, lazy } from "react";
import { Navigate } from "react-router-dom";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import Home from "../pages/Home/Home";
import Login from "../pages/Login/Login";
import NotFound from "../pages/NotFound/NotFound";
import { ModalConfig } from "../components/Contexts/GlobalModalContext";
import { AreaKey, SectionKey, ViewKey } from "./helpers/pageVocabulary.generated";
import { AREAS_WITH_WRITES } from "./helpers/writeVocabulary.generated";
import { routeArea } from "./helpers/routeAccess";
import DiggingLoadingSummaryPage from "../pages/ChatreeProduction/DiggingLoadingSummaryPage/DiggingLoadingSummaryPage";
import Production from "../pages/ChatreeProduction/Production";
import GeologyCrushing from "../pages/GeologyCrushing/GeologyCrushing";
import GeologyRehandle from "../pages/GeologyRehandle/GeologyRehandle";
import Operations from "../pages/Operations/Operations";
import Stoppages from "../pages/Stoppages/Stoppages";

const Analytes = lazy(() => import("../pages/Admin/References/Analytes/Analytes"));
const BlastMaterial = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterial"));
const BlastMaterialType = lazy(() => import("../pages/Admin/References/Blasts/BlastMaterialType"));
const BlastPattern = lazy(() => import("../pages/Admin/References/Blasts/BlastPattern/BlastPattern"));
const BlastPatternPage = lazy(() => import("../pages/DrillBlast/Pattern/BlastPatternPage"));
const FiringPatterns = lazy(() => import("../pages/DrillBlast/Firing/FiringPatterns"));
const ExplosivesStore = lazy(() => import("../pages/DrillBlast/Explosives/ExplosivesStore"));
const ExplosivesRegister = lazy(() => import("../pages/DrillBlast/Explosives/ExplosivesRegister"));
const Magazines = lazy(() => import("../pages/DrillBlast/Magazines/Magazines"));
const BlastReferences = lazy(() => import("../pages/DrillBlast/References/BlastReferences"));
const DrillConsumables = lazy(() => import("../pages/DrillBlast/Consumables/DrillConsumables"));
const ConsumablesStock = lazy(() => import("../pages/DrillBlast/Consumables/ConsumablesStock"));
const Personnel = lazy(() => import("../pages/DrillBlast/Personnel/Personnel"));
const DrillBlastSettingsPage = lazy(() => import("../pages/DrillBlast/Settings/DrillBlastSettingsPage"));
const RcDrilling = lazy(() => import("../pages/DrillBlast/RcDrilling/RcDrilling"));
const BucketFactors = lazy(() => import("../pages/Admin/References/BucketFactors/BucketFactors"));
const BucketTypes = lazy(() => import("../pages/Admin/References/BucketTypes/BucketTypes"));
const Densities = lazy(() => import("../pages/Admin/References/Densities/Densities"));
const BlastHoleDrilling = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrilling"));
const BlastHoleDrillingList = lazy(() => import("../pages/Drilling/BlastHoleDrilling/BlastHoleDrillingList"));
const GeologyMilling = lazy(() => import("../pages/GeologyMilling/GeologyMilling"));
const GeologyOreBlocks = lazy(() => import("../pages/GeologyOreBlocks/GeologyOreBlocks"));
const GeologyOreTally = lazy(() => import("../pages/GeologyOreTally/GeologyOreTally"));
const RoyaltySplits = lazy(() => import("../pages/RoyaltySplits/RoyaltySplits"));
const StockpileBalances = lazy(() => import("../pages/StockpileBalances/StockpileBalances"));
const Zones = lazy(() => import("../pages/Admin/References/Zones/Zones"));
const MaterialTypes = lazy(() => import("../pages/Admin/References/MaterialTypes/MaterialTypes"));
const Plants = lazy(() => import("../pages/Admin/References/Plants/Plants"));
const DowntimeSubtypes = lazy(() => import("../pages/Admin/References/DowntimeSubTypes/DownTimeSubTypes"));
const PitStages = lazy(() => import("../pages/Admin/References/PitStages/PitStages"));
const Targets = lazy(() => import("../pages/Admin/References/Targets/Targets"));
const MinePeriods = lazy(() => import("../pages/Admin/References/MinePeriods/MinePeriods"));
const DashboardTiles = lazy(() => import("../pages/Admin/References/DashboardTiles/DashboardTiles"));
const SectionNames = lazy(() => import("../pages/Admin/References/SectionNames/SectionNames"));
const TargetTypes = lazy(() => import("../pages/Admin/References/TargetTypes/TargetTypes"));
const DailyReport = lazy(() => import("../pages/Reports/DailyReport/DailyReport"));
const GeologyDrilling = lazy(() => import("../pages/GeologyDrilling/GeologyDrilling"));
const Users = lazy(() => import("../pages/Admin/References/Users/Users"));
const MaterialTypeClasses = lazy(() => import("../pages/Admin/References/MaterialTypeClasses/MaterialTypeClasses"));
const PlantTypes = lazy(() => import("../pages/Admin/References/PlantTypes/PlantTypes"));
const Locations = lazy(() => import("../pages/Admin/References/Locations/Locations"));
const DeviceMappings = lazy(() => import("../pages/Admin/References/DeviceMappings/DeviceMappings"));
const Dewatering = lazy(() => import("../pages/Dewatering/Dewatering"));
const GeologyRecalcModal = lazy(() => import("../pages/GeologyRecalcModal/GeologyRecalcModal").then(m => ({ default: m.GeologyRecalcModal })));
const MorningReport = lazy(() => import("../pages/Reports/MorningReport/MorningReport"));
const AccountPage = lazy(() => import("../pages/Account/Account"));
const ApiKeys = lazy(() => import("../pages/Account/ApiKeys/ApiKeys"));
const RoyaltyAreaBalances = lazy(() => import("../pages/RoyaltyAreaBalances/RoyaltyAreaBalances").then(m => ({ default: m.RoyaltyAreaBalances })));
const GeologyProduction = lazy(() => import("../pages/GeologyProduction/GeologyProduction/GeologyProduction"));
const GeoMiningMvtSummaryList = lazy(() => import("../pages/GeologyProduction/GeoMiningMvtSummaryList/GeoMiningMvtSummaryList"));
const TargetImportList = lazy(() => import("../pages/Targets/Import/TargetImportList"));
const TargetImportTemplateEditor = lazy(() => import("../pages/Targets/Import/TargetImportTemplateEditor"));
const TargetImportRun = lazy(() => import("../pages/Targets/Import/TargetImportRun"));
const TargetImportAnalyze = lazy(() => import("../pages/Targets/Import/TargetImportAnalyze"));
const ReportLibraryPage = lazy(() => import("../pages/Reports/ReportLibraryPage/ReportLibraryPage"));
const ReportBuilderPage = lazy(() => import("../pages/Reports/ReportBuilderPage/ReportBuilderPage"));
const ReportViewPage = lazy(() => import("../pages/Reports/ReportViewPage/ReportViewPage"));
const DataConnectionsPage = lazy(() => import("../pages/Reports/DataConnectionsPage/DataConnectionsPage"));
const MachinePositionsMap = lazy(() => import("../pages/Telemetry/MachinePositionsMap"));
const UncheckedGpsStoppages = lazy(() => import("../pages/Stoppages/UncheckedGpsStoppages"));

export interface AppRouteConfig {
  path: string;
  label?: string; // Note: only for a nav group that is neither a page nor a section
  icon?: IconDefinition; // Note: Optional per-route override; otherwise resolved from routeIcons.ts by path
  ignore?: boolean;
  element?: JSX.Element;
  action?: (showModal: (config: ModalConfig) => void) => void; // For routes that trigger an action instead of navigation
  protected?: boolean;
  view?: ViewKey; // Note: name comes from VIEW_NAMES
  alsoOpenTo?: readonly AreaKey[];
  section?: SectionKey; // Note: top-level nav grouping; name comes from SECTION_NAMES
  adminOnly?: boolean;
  leadAdminOnly?: boolean;
  isShiftPage?: boolean;
  alsoHasEditControlsFor?: AreaKey[];
  // Note: a Lead Admin reaches the page without its area, for the part of it they administer; the page
  // decides what else they may do there.
  openToLeadAdmins?: boolean;
  children?: AppRouteConfig[];
}

/** The areas whose edit rights decide whether a page is view only: its own and any it hosts controls
 *  for, wherever the API has a write behind them. None for a page with nothing to change. */
export const editControlAreas = (page: AppRouteConfig | undefined): AreaKey[] =>
  [routeArea(page?.view), ...(page?.alsoHasEditControlsFor ?? [])]
    .filter((area): area is AreaKey => !!area && AREAS_WITH_WRITES.has(area));

export function createAppRoutes(
  { telemetryEnabled = false, drillBlastModules = [] }: { telemetryEnabled?: boolean; drillBlastModules?: string[] } = {}
): AppRouteConfig[] {
  return [
    {
      path: "/login", view: "Login",
      element: <Login />,
      protected: false,
      ignore: true
    },
    {
      path: "/", view: "Home",
      element: <Home />,
      protected: true,
    },
    {
      path: "/account", view: "Account",
      element: <AccountPage />,
      protected: true,
      ignore: true,
    },
    {
      path: "/api-keys", view: "ApiKeys",
      element: <ApiKeys />,
      protected: true,
      ignore: true,
      adminOnly: true
    },
    {
      path: "operations", section: "operations", element: <Operations />, protected: true,
      view: "Operations", isShiftPage: true, alsoHasEditControlsFor: ["Stoppages"]
    },
    {
      path: "production", section: "production", protected: true,
      children: [
        {
          path: "show",
          element: <Production />,
          protected: true,
          view: "Production", isShiftPage: true,
        },
        {
          path: "list",
          element: <DiggingLoadingSummaryPage />,
          protected: true,
          view: "DiggingLoadingSummary",
        },
        {
          path: "rehandle", element: <GeologyRehandle />, protected: true,
          view: "GeologyRehandle", isShiftPage: true
        },
        {
          path: "crushing", element: <GeologyCrushing />, protected: true,
          view: "GeologyCrushing", isShiftPage: true
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          view: "ProductionOreTally", isShiftPage: true
        },
      ]
    },
    {
      path: "stoppages", section: "stoppages", element: <Stoppages />, protected: true,
      view: "Stoppages", isShiftPage: true
    },
    // Note: spread rather than a `hidden` flag, so on a site without an FMS the section's routes do not exist
    // at all - no menu entry AND no reachable URL. A flag the nav merely skipped would still leave each page
    // open to anyone who typed the path. Absent telemetryEnabled reads as off.
    ...(telemetryEnabled ? [{
      path: "telemetry", label: "Telemetry", protected: true,
      children: [
        {
          path: "unchecked-gps-stoppages", element: <UncheckedGpsStoppages />, protected: true,
          view: "UncheckedGpsStoppages" as const, isShiftPage: true
        },
        {
          path: "machine-positions", element: <MachinePositionsMap />, protected: true,
          view: "MachinePositions" as const
        },
        {
          path: "device-mappings", element: <DeviceMappings />, protected: true,
          view: "DeviceMappings" as const
        },
      ]
    }] : []),
    {
      path: "db", section: "db", protected: true,
      children: [
        {
          path: "bh-drilling", element: <BlastHoleDrilling />, protected: true,
          view: "BlastHoleDrilling", isShiftPage: true
        },
        {
          path: "bh-drilling-list", element: <BlastHoleDrillingList />, protected: true,
          view: "BlastHoleDrillingList"
        },
        {
          path: "blasts",
          protected: true,
          label: "Blasts",
          children: [
            {
              path: "pattern", element: <BlastPattern />, protected: true,
              view: "BlastPattern"
            },
            {
              path: "pattern/:id", element: <BlastPatternPage />, protected: true,
              ignore: true, view: "BlastPattern", alsoOpenTo: ["BlastFiring"], alsoHasEditControlsFor: ["BlastFiring"]
            },
            {
              path: "material", element: <BlastMaterial />, protected: true,
              view: "BlastMaterial"
            },
            {
              path: "material-type", element: <BlastMaterialType />, protected: true,
              view: "BlastMaterialType"
            }
          ]
        },
        {
          path: "firing", element: <FiringPatterns />, protected: true,
          view: "BlastFiring"
        },
        {
          path: "dewatering", element: <Dewatering />, protected: true,
          view: "Dewatering", isShiftPage: true
        },
        ...(drillBlastModules.includes("GradeControl") ? [{
          path: "rc-drilling", element: <RcDrilling />, protected: true,
          view: "RcDrilling" as const, isShiftPage: true
        }] : []),
        ...(drillBlastModules.includes("ExplosivesStore") ? [{
          path: "explosives", element: <ExplosivesStore />, protected: true,
          view: "ExplosivesStore" as const
        }, {
          path: "explosives-register", element: <ExplosivesRegister />, protected: true,
          view: "ExplosivesRegister" as const
        }, {
          path: "magazines", element: <Magazines />, protected: true,
          view: "Magazines" as const
        }] : []),
        {
          path: "references", element: <BlastReferences />, protected: true,
          view: "BlastReference" as const
        },
        ...(drillBlastModules.includes("Consumables") ? [{
          path: "consumables", element: <DrillConsumables />, protected: true,
          view: "DrillConsumables" as const, isShiftPage: true
        }, {
          path: "consumables/stock", element: <ConsumablesStock />, protected: true,
          view: "DrillConsumableStock" as const
        }] : []),
        {
          path: "personnel", element: <Personnel />, protected: true,
          view: "Personnel"
        },
        {
          path: "settings", element: <DrillBlastSettingsPage />, protected: true,
          view: "DrillBlastSettings"
        },
      ]
    },
    {
      path: "geology", section: "geology", protected: true,
      children: [
        {
          path: "unused", protected: true,
          action: (showModal) => showModal({ content: () => <GeologyRecalcModal /> }), view: "RecalculateStockpiles"
        },
        {
          path: "production", element: <GeologyProduction />, protected: true,
          view: "ShowMiningMvt", isShiftPage: true
        },
        {
          path: "mining-movements", element: <GeoMiningMvtSummaryList />, protected: true,
          view: "MiningMvtList"
        },
        {
          path: "stockpile-balances", element: <StockpileBalances />, protected: true,
          view: "StockpileBalances"
        },
        {
          path: "ore-blocks", element: <GeologyOreBlocks />, protected: true,
          view: "GeologyOreBlocks"
        },
        {
          path: "ore-tally", element: <GeologyOreTally />, protected: true,
          view: "GeologyOreTally", isShiftPage: true
        },
        {
          path: "milling", element: <GeologyMilling />, protected: true,
          view: "GeologyMilling", isShiftPage: true
        },
        {
          path: "drilling", element: <GeologyDrilling />, protected: true,
          view: "GeologyDrilling", isShiftPage: true
        },
      ]
    },
    {
      path: "reports", section: "reports",
      protected: true,
      children: [
        {
          path: "daily", element: <DailyReport />, protected: true, view: "DailyReport",
        },
        {
          path: "morning", element: <MorningReport />, protected: true, view: "MorningReport",
        },
        {
          path: "library", element: <ReportLibraryPage />, protected: true, view: "ReportLibrary",
        },
        {
          path: "builder", element: <ReportBuilderPage />, protected: true, view: "ReportBuilder",
        },
        {
          path: "builder/:id", element: <ReportBuilderPage />, protected: true,
          ignore: true, view: "ReportBuilder",
        },
        {
          path: "view/:id", element: <ReportViewPage />, protected: true,
          ignore: true, view: "ReportLibrary",
        },
        {
          path: "data-connections", element: <DataConnectionsPage />, protected: true, view: "ReportDataConnections",
        }
      ]
    },
    {
      path: "royalty-splits", section: "royalty-splits", element: <RoyaltySplits />, protected: true,
      view: "RoyaltySplits"
    },
    {
      path: "royalty-area-balances", section: "royalty-area-balances", element: <RoyaltyAreaBalances />, protected: true,
      view: "RoyaltyAreaBalances"
    },
    {
      path: "targets/imports", element: <Navigate to="/admin/references/target-refs/imports" replace />, ignore: true
    },
    {
      path: "admin", section: "admin",
      protected: true,
      children: [
        {
          path: "users", view: "Users", element: <Users />, protected: true,
          adminOnly: true
        },
        {
          path: "references", label: "References",
          protected: true,
          children: [
            {
              path: "analytes", element: <Analytes />, protected: true,
              view: "Analytes"
            },
            {
              path: "bucket-factors", element: <BucketFactors />, protected: true,
              view: "BucketFactors"
            },
            {
              path: "bucket-types", element: <BucketTypes />, protected: true,
              view: "BucketTypes"
            },
            {
              path: "dashboard-tiles", view: "DashboardTiles", element: <DashboardTiles />, protected: true,
              leadAdminOnly: true
            },
            {
              path: "densities", element: <Densities />, protected: true,
              view: "Densities"
            },
            {
              path: "downtime-subtypes", element: <DowntimeSubtypes />, protected: true,
              view: "DowntimeSubtypes"
            },
            {
              path: "locations", element: <Locations />, protected: true,
              view: "Locations"
            },
            {
              path: "material-type-classes", element: <MaterialTypeClasses />, protected: true,
              view: "MaterialTypeClasses"
            },
            {
              path: "material-types", element: <MaterialTypes />, protected: true,
              view: "MaterialTypes"
            },
            {
              path: "mine-periods", view: "MinePeriods", element: <MinePeriods />, protected: true,
              leadAdminOnly: true
            },
            {
              path: "pit-stages", element: <PitStages />, protected: true,
              view: "PitStages"
            },
            {
              path: "plant", element: <Plants />, protected: true,
              view: "Plants"
            },
            {
              path: "plant-types", element: <PlantTypes />, protected: true,
              view: "PlantTypes"
            },
            {
              path: "section-names", view: "SectionNames", element: <SectionNames />, protected: true,
              leadAdminOnly: true
            },
            {
              path: "target-refs", label: "Targets", protected: true,
              children: [
                {
                  path: "imports", view: "TargetImport",
                  element: <TargetImportList />,
                  protected: true
                },
                {
                  path: "imports/analyze", view: "TargetImport",
                  element: <TargetImportAnalyze />,
                  protected: true,
                  ignore: true
                },
                {
                  path: "imports/templates/new", view: "TargetImport",
                  element: <TargetImportTemplateEditor />,
                  protected: true,
                  ignore: true
                },
                {
                  path: "imports/templates/:id", view: "TargetImport",
                  element: <TargetImportTemplateEditor />,
                  protected: true,
                  ignore: true
                },
                {
                  path: "imports/run/:templateId", view: "TargetImport",
                  element: <TargetImportRun />,
                  protected: true,
                  ignore: true
                },
                {
                  path: "target-types", view: "TargetTypes", element: <TargetTypes />, protected: true
                },
                {
                  path: "targets", element: <Targets />, protected: true,
                  view: "Targets"
                },
              ]
            },
            {
              path: "zones", element: <Zones />, protected: true,
              view: "Zones", openToLeadAdmins: true
            },
            {
              path: "page-access", element: <Navigate to="/admin/references/zones" replace />, ignore: true
            },
            ...(telemetryEnabled ? [{
              path: "device-mappings", element: <Navigate to="/telemetry/device-mappings" replace />, ignore: true
            }] : []),
          ]
        }
      ]
    },
    {
      path: "*",
      element: <NotFound />,
      protected: true
    }
  ];
}
